import Extension from '../base/extension';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';

class ShopifyStorefrontApi extends Extension {
  #client;

  constructor(name) {
    super(name);

    Stencil.registerHelper("idToGraphQlId", this.#idToGraphQlId, this);

    let key = document.querySelector("body").dataset.storefrontApiKey;
    if(!key){
      console.warn(`${this.logPrefix}`, `No Storefront API key found`);
      return;
    }

    this.#client = createStorefrontApiClient({
      storeDomain: location.origin,
      apiVersion: '2025-01',
      publicAccessToken: key,
    });
    
    Stencil.registerHelper("executeGQL", this.#executeGQL, this);
    Stencil.registerHelper("getAllNodes", this.#getAllNodes, this);
  }

  async #executeGQL(query, options = {}) {
    return await this.#client.request(query, options);
  }

  /**
   * Get all nodes, from all pages, for a given node-type query
   * 
   * Automatically handles pagination by checking for `pageInfo` and `endCursor`
   * in the response, and continues to query until `hasNextPage` is false.
   * 
   * @param {String} query 
   * @param {Object} options 
   * @returns {Promise<Array>}
   */
  async #getAllNodes(query, options = {}) {
    const allData = [];
    let endCursor = null;
    let hasNextPage = true;
  
    do {
      const variables = { ...options.variables, after: endCursor };
      const result = await this.#executeGQL(query, { ...options, variables });
  
      const [topLevelKey] = Object.keys(result?.data || {});
      const { nodes = [], pageInfo = {} } = result?.data?.[topLevelKey] || {};
  
      if (!nodes || !pageInfo) {
        throw new Error('Query result does not have the expected "nodes" or "pageInfo" structure.');
      }
  
      allData.push(...nodes);
      endCursor = pageInfo.endCursor;
      hasNextPage = pageInfo.hasNextPage; // Update `hasNextPage` for the loop condition
    } while (hasNextPage);
  
    return allData;
  }

  #idToGraphQlId(id, type) {
    switch(type){
      case "variant": type = "ProductVariant"; break;
      case "product": type = "Product"; break;
      case "metaobject": type = "Metaobject"; break;
    }
    return `gid://shopify/${type}/${id}`;
  }

  async example() {
    const productQuery = `
      query ProductQuery($handle: String) {
        product(handle: $handle) {
          id
          title
          handle
        }
      }
    `;

    const {data, errors, extensions} = await this.#client.request(productQuery, {
      variables: {
        handle: 'trtl-pillow-cool',
      },
    });

    console.log(data, errors, extensions);
  }
}

export default ShopifyStorefrontApi;